import type {
  IFilter,
  IPaginationQuery,
  IPaginationResponse,
  SortDirection,
  WithRenderCallback,
} from '@wyz/types';
import clsx from 'clsx';
import { isEmpty, isFunction, isNil, pickBy } from 'lodash-es';

import * as React from 'react';
import { Table as BTable, Spinner } from 'react-bootstrap';

import {
  DatatableConfigurationContext,
  createDataTableContext,
  useDatatableContext,
  useGlobalConfiguration,
} from './context';
import DatatableErrorBoundary from './error-boundary';
import useDatatable from './hook';
import './style.scss';
import { TableCell } from './table-cell';
import {
  CellParams,
  ColumnDefinition,
  DatatableProps,
  IDatatableGlobalConfiguration,
  RowModel,
} from './types';
import { getRowIdentifier, isEqualCondition } from './utils';
import { useArray } from '@wyz/utils';
import { Tooltip } from '../index';
const getOrder = ({
  sort,
  field,
}: {
  field: string;
  sort: IPaginationQuery['sort'];
}): SortDirection | undefined => {
  const sortField = sort?.sortField;
  if (sortField !== field) return 'ASC';
  else if (sort?.sortDirection === 'ASC') return 'DESC';
  return undefined;
};

type RenderPropData<TDataItem, TCriteria> = {
  data: IPaginationResponse<TDataItem> | undefined;
  criteria: TCriteria;
  isEmpty: boolean;
};
const DatatableProvider = <TDataItem extends RowModel, TCriteria = IFilter>({
  children,
  columns,
  fetcher,
  queryKey,
  defaults,
  translations,
  queryTransformer,
}: WithRenderCallback<
  Pick<
    DatatableProps<TDataItem, TCriteria>,
    | 'columns'
    | 'fetcher'
    | 'queryKey'
    | 'defaults'
    | 'translations'
    | 'queryTransformer'
  >,
  RenderPropData<TDataItem, TCriteria>
>): React.ReactElement<unknown> => {
  const filterableColumns = columns.filter((column) => column.filterable);
  const datatable = useDatatable<TDataItem, TCriteria>({
    fetcher,
    queryTransformer,
    columns,
    queryKey,
    defaults: {
      ...defaults,
      criteria: {
        operator: 'and',
        conditions: filterableColumns.map((column) => {
          const defaultValue = defaults?.criteria?.conditions?.find(
            (condition) => isEqualCondition(condition, column),
          )?.value;
          return {
            column: getRowIdentifier(column),
            operator:
              column.filterable &&
              'isMulti' in column.filter &&
              column.filter.isMulti
                ? 'in'
                : 'eq',
            value: defaultValue,
          };
        }),
      },
    },
  });
  const Context = createDataTableContext<TDataItem, TCriteria>();
  const DEFAULT_KEYS = ['operator', 'conditions'];
  const isFiltered =
    Object.keys(
      pickBy(
        datatable.state.transformedCriteria || {},
        (value) => !isEmpty(value),
      ),
    ).filter((key) => !DEFAULT_KEYS.includes(key)).length > 0;

  return (
    <Context.Provider
      value={{
        internals: datatable,
        columns,
        translations,
        defaults,
      }}
    >
      {isFunction(children)
        ? children({
            data: datatable.query.data,
            criteria: datatable.state.transformedCriteria,
            isEmpty: datatable.query.data?.meta.total === 0 && !isFiltered,
          })
        : children}
    </Context.Provider>
  );
};

type SelectRowEvent<TDataItem extends RowModel> =
  | {
      type: 'select';
      row: TDataItem;
      currentSelection: Array<TDataItem>;
    }
  | {
      type: 'deselect';
      row: TDataItem;
      currentSelection: Array<TDataItem>;
    }
  | {
      type: 'select-all';
      rows: Array<TDataItem>;
      currentSelection: Array<TDataItem>;
    }
  | {
      type: 'deselect-all';
      currentSelection: Array<TDataItem>;
    };
export const Table = <TDataItem extends RowModel>({
  stickyHeader,
  hoverable,
  styleless,
  keyGetter = (item) => item.id,
  children,
  renderExpanded,
  defaultExpanded = [],
  onSelectRow,
  isRowSelectable,
  selectedRows,
  striped = true,
  bordered = false,
}: React.PropsWithChildren<
  Pick<DatatableProps<TDataItem>, 'keyGetter' | 'hoverable'> & {
    stickyHeader?: boolean;
    styleless?: boolean;
    renderExpanded?: (
      parameters: Pick<CellParams<TDataItem>, 'row'>,
    ) => React.ReactElement<unknown>;
    defaultExpanded?: Array<string>;
    onSelectRow?: (parameters: SelectRowEvent<TDataItem>) => void;
    isRowSelectable?: (row: TDataItem) => boolean;
    selectedRows?: Array<string>;
    striped?: boolean;
    bordered?: boolean;
  }
>) => {
  const multiSelectionEnabled = Boolean(onSelectRow && selectedRows);
  const { internals, columns, translations } = useDatatableContext<TDataItem>();
  const [expandedRows, expandedRowsHandlers] =
    useArray<string>(defaultExpanded);

  const isLoading = internals.query.isLoading || internals.query.isFetching;
  const visibleColumns = columns.filter((column) => !column.hide);
  function handleSort(column: ColumnDefinition<TDataItem>) {
    const order = getOrder({
      sort: internals.state.sort,
      field: column.field,
    });
    internals.setter((prevState) => ({
      ...prevState,
      sort: isNil(order)
        ? undefined
        : {
            sortField: column.field,
            sortDirection: order,
          },
    }));
  }

  function getIcon(column: ColumnDefinition<TDataItem>) {
    const sortField = internals.state.sort?.sortField;

    if (
      column.field === sortField &&
      internals.state.sort?.sortDirection === 'DESC'
    )
      return <span className='ms-2 fs-xs border'>⬆</span>;
    else if (
      column.field === sortField &&
      internals.state.sort?.sortDirection === 'ASC'
    )
      return <span className='ms-2 fs-xs border'>⬇</span>;
    return undefined;
  }
  const currentSelectedRows =
    internals.query.data?.data.filter((item) =>
      selectedRows?.includes(keyGetter(item)),
    ) || [];
  const isAllSelected =
    multiSelectionEnabled &&
    selectedRows?.length === internals.query.data?.data.length;
  return (
    <BTable
      className={clsx('datatable overflow-auto', {
        loading: isLoading,
        datatable: !styleless,
      })}
      style={{ tableLayout: 'fixed' }}
      striped={striped}
      bordered={bordered}
    >
      <thead
        className={clsx('table-head text-center align-middle', {
          sticky: stickyHeader,
        })}
      >
        <tr>
          {multiSelectionEnabled && (
            <th
              className={clsx('px-1', {
                'border-bottom': !styleless,
              })}
              style={{ width: '50px' }}
            >
              <div className='d-flex justify-content-start align-items-center'>
                <input
                  type='checkbox'
                  data-cy='select-all-rows'
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allRows = (internals.query.data?.data || []).filter(
                        (dataItem) =>
                          !isRowSelectable || isRowSelectable(dataItem),
                      );
                      onSelectRow?.({
                        type: 'select-all',
                        rows: allRows,
                        currentSelection: allRows,
                      });
                    } else {
                      onSelectRow?.({
                        type: 'deselect-all',
                        currentSelection: [],
                      });
                    }
                  }}
                  checked={isAllSelected}
                />
                <span className='text-left fw-semi-bold ms-2 fs-xxs'>
                  {isAllSelected && (
                    <>
                      {translations?.selectedLabel
                        ? translations?.selectedLabel({
                            selected: selectedRows?.length || 0,
                            total: internals.query.data?.meta.total || 0,
                          })
                        : `Selected ${selectedRows?.length || 0} of ${internals.query.data?.meta.total || 0}`}
                    </>
                  )}
                  {(!isAllSelected && translations?.selectAllLabel) ??
                    'Select All'}
                </span>
              </div>
            </th>
          )}
          {visibleColumns.map((column) => (
            <th
              key={column.field}
              id={column.field}
              className={clsx('fw-bold', {
                'border-bottom': !styleless,
                'text-center': column.headerAlign === 'center',
                'text-right': column.headerAlign === 'right',
                'text-left':
                  column.headerAlign === 'left' || !column.headerAlign, // default to left
              })}
              onClick={column.sortable ? () => handleSort(column) : undefined}
              {...(column.width && {
                style: {
                  minWidth: column.width,
                  maxWidth: column.width,
                  width: column.width,
                },
              })}
            >
              {column.sortable ? (
                <div className='text-truncate'>
                  <Tooltip label={column.headerName}>
                    <span>{column.headerName}</span>
                  </Tooltip>
                  {getIcon(column)}
                </div>
              ) : (
                <Tooltip label={column.headerName}>
                  <span>{column.headerName}</span>
                </Tooltip>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className='table-body'>
        {!isLoading && internals.query.data?.data?.length === 0 && (
          <tr>
            <td colSpan={visibleColumns.length} className='text-center'>
              {translations?.emptyMessageText ?? 'No data'}
            </td>
          </tr>
        )}
        {isLoading && (
          <tr>
            <td colSpan={visibleColumns.length} className='text-center'>
              <Spinner />
            </td>
          </tr>
        )}
        {(internals.query.data?.data || []).map((dataItem, dataItemIndex) => {
          const key = keyGetter(dataItem);
          const isRowExpanded = expandedRows.includes(key);
          const close = () => expandedRowsHandlers.removeByItem(key);
          const open = () => expandedRowsHandlers.append(key);
          return (
            <React.Fragment key={`row-${key}`}>
              <tr
                className={clsx('align-middle', {
                  hoverable: hoverable,
                  active: isRowExpanded,
                })}
              >
                {multiSelectionEnabled && (
                  <td className='px-1' style={{ width: '50px' }}>
                    {(!isRowSelectable || isRowSelectable(dataItem)) && (
                      <div className='d-flex justify-content-start align-items-center'>
                        <input
                          type='checkbox'
                          data-cy={`select-row-${dataItemIndex}`}
                          checked={selectedRows?.includes(key)}
                          onChange={(e) => {
                            let nextRows: Array<TDataItem> = [];
                            if (e.target.checked) {
                              nextRows.push(...currentSelectedRows, dataItem);
                            } else {
                              nextRows = currentSelectedRows.filter(
                                (item) => keyGetter(item) !== key,
                              );
                            }
                            onSelectRow?.({
                              type: e.target.checked ? 'select' : 'deselect',
                              row: dataItem,
                              currentSelection: nextRows,
                            });
                          }}
                        />
                      </div>
                    )}
                  </td>
                )}
                {visibleColumns.map((column) => {
                  return (
                    <React.Fragment key={`row-${key}-${column.field}`}>
                      <TableCell
                        key={`row-${key}-${column.field}`}
                        dataItem={dataItem}
                        dataItemIndex={dataItemIndex}
                        column={column}
                        expandability={{
                          close,
                          open,
                          toggle: isRowExpanded ? close : open,
                          isExpanded: isRowExpanded,
                        }}
                      />
                    </React.Fragment>
                  );
                })}
              </tr>
              {isRowExpanded && renderExpanded && (
                <tr className='expanded'>
                  <td colSpan={visibleColumns.length}>
                    {renderExpanded({
                      row: dataItem,
                    })}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
        {children}
      </tbody>
    </BTable>
  );
};

export const ConfigurationProvider = ({
  children,
  ...globals
}: React.PropsWithChildren<IDatatableGlobalConfiguration>) => {
  return (
    <DatatableConfigurationContext.Provider value={globals}>
      {children}
    </DatatableConfigurationContext.Provider>
  );
};

export const Container = <TDataItem extends RowModel, TCriteria = IFilter>({
  children,
  ...props
}: WithRenderCallback<
  DatatableProps<TDataItem, TCriteria>,
  RenderPropData<TDataItem, TCriteria>
>): React.ReactElement<unknown> => {
  const globalConfig = useGlobalConfiguration();
  return (
    <DatatableErrorBoundary containerStyles={props.containerStyles}>
      <DatatableProvider<TDataItem, TCriteria>
        {...props}
        translations={{ ...globalConfig.translations, ...props.translations }}
      >
        {(state) => <>{isFunction(children) ? children(state) : children}</>}
      </DatatableProvider>
    </DatatableErrorBoundary>
  );
};
